import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ForceGraph2D, { ForceGraphMethods, LinkObject, NodeObject } from "react-force-graph-2d";
import { Tooltip, useTheme } from "@mui/material";
import * as d3 from "d3";
import { Box } from "@mui/system";
import { useTranslation } from "react-i18next";

import ForceGraphTooltip from "./ForceGraphTooltip";
import { useResizeDetector } from "react-resize-detector";

interface ForceGraphProps {
  colors?: Array<string | CanvasGradient | CanvasPattern>;
  data: ForceGraphResponse;
  displayLinks?: boolean;
  labelDisplay?: 0 | 1 | 2;
  linkColorFunction?: (node: SimulationLink, canvas: CanvasRenderingContext2D) => string | CanvasGradient | CanvasPattern;
  nodeColorFunction?: (node: SimulationNode, canvas: CanvasRenderingContext2D) => string | CanvasGradient | CanvasPattern;
  onClick?: (node: Node, cluster: Cluster) => void;
  height: number;
  width: number;
}

interface SampleRecord {
  published: string;
  source: number;
  body: string;
  link: string;
  author: string;
  language: string;
  sentiment: "negative" | "neutral" | "positive";
  entities: Array<string>;
  words: number;
  _analyzed_text: string;
}

export interface Cluster {
  label: string;
  count: number;
  distribution: number;
  est_total_count: number;
  positive_count: number;
  negative_count: number;
  est_total_positive_count: number;
  est_total_negative_count: number;
  positivity: number;
  query: any;
  sentimental: number;
  timeline: Array<number>;
  timeline_distribution: Array<number>;
  key_topics: Array<string>;
  sample_records: Array<SampleRecord>;
}

export interface Link {
  /**
   * The `id` of the source `Node`.
   */
  source: string;
  /**
   * The `id` of the target `Node`.
   */
  target: string;
  /**
   * The weight of the link.
   */
  w: number;
}

/**
 * The SimulationLink is produced by the force graph.  The force graph takes a `Link` as input and 
 * overrides properties on the existing object with new values.  The `Link` becomes a 
 * `SimulationLink` once it has run through the force graph.
 */
export interface SimulationLink {
  index: number;
  source: SimulationNode;
  target: SimulationNode;
  w: number;
}

export interface Node {
  id: string;
  /**
   * The node value.
   */
  val: number;
  /**
   * The node's color group.  All nodes with the same color group should also have the same color.
   */
  c: number;
  /**
   * The sentimental color where: `1` = neutral, `2` = positive, and `3` = negative.
   */
  s: 1 | 2 | 3;
  /**
   * The node label display where: `undefined` = no label, `1` = key cluster, `2` = cluster
   */
  l: undefined | 1 | 2;
}

/**
 * The SimulationNode is produced by the force graph.  The force graph takes a `Node` as input and
 * overrides the properties on the existing object with new values.  The `Node` becomes a 
 * `SimulationNode` once it has run through the force graph.
 */
export interface SimulationNode extends Node {
  vx: number;
  vy: number;
  x: number;
  y: number;
}

interface SimulationNodeDatum extends Node, d3.SimulationNodeDatum {}

export interface ForceGraphResponse {
  total_count: number;
  analyzed_count: number;
  count_is_exact: boolean;
  word_count: number;
  est_total_words: number;
  analyzed_fields: Array<string>;
  positive_count: number;
  negative_count: number;
  est_total_positive_count: number;
  est_total_negative_count: number;
  positivity: number;
  min_timestamp: string;
  max_timestamp: string;
  timeline_timestamps: Array<string>;
  timeline: Array<number>;
  timeline_distribution: Array<number>;
  clusters: Array<Cluster>;
  links: Array<Link>;
  nodes: Array<Node>;
}

export default function ForceGraph(props: ForceGraphProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();
  
  const forceGraphRef = useRef<ForceGraphMethods<NodeObject<Node>, LinkObject<Node, Link>> | undefined>();
  const quadtree = useRef<d3.Quadtree<SimulationNode> | null>(null);

  const onResize = useCallback(() => forceGraphRef.current?.zoomToFit(400), []);

  const { width, height, ref } = useResizeDetector({
    onResize: onResize
  });

  const [hoveredNode, setHoveredNode] = useState<SimulationNode | null>(null);

  const displayLinks = props.displayLinks ?? true;
  const labelDisplay = props.labelDisplay ?? 1;
  const defaultColors = theme.colors.namedColors.chartColorArray;
  const colors = props.colors || defaultColors;

  const defaultNodeColorFunction = useCallback((node: SimulationNode, canvas: CanvasRenderingContext2D): string | CanvasGradient | CanvasPattern => {
    return colors[node.c % defaultColors.length];
  }, []);
  const defaultLinkColorFunction = useCallback((link: SimulationLink, canvas: CanvasRenderingContext2D): string | CanvasGradient | CanvasPattern => {
    return colors[link.source.c % defaultColors.length];
  }, []);
  const linkColorFunction = props.linkColorFunction || defaultLinkColorFunction;
  const nodeColorFunction = props.nodeColorFunction || defaultNodeColorFunction;

  // Scale is currently only being used to manually trigger a re-render of the force graph.  This
  // is somewhat of a hack but allows us to keep the default `autoPauseRedraw={true}` which results
  // in a substantial performance improvement.
  const scale = useRef<number>(1);

  const nodeLabelGroups: Array<Array<SimulationNode>> = useMemo(() => {
    const groups: Array<Array<SimulationNode>> = [
      [], // Important cluster labels
      []  // Regular cluster labels
    ];
    for (const node of props.data.nodes) {
      if (node.l) {
        groups[node.l-1].push(node as SimulationNode);
      }
    }
    return groups;
  }, []);

  useEffect(() => {
    forceGraphRef.current?.d3Force("charge", d3.forceManyBody().strength(-70));
    forceGraphRef.current?.d3Force("gravity-x", d3.forceX(width ?? 0 / 2).strength(0.08));
    forceGraphRef.current?.d3Force("gravity-y", d3.forceY(width ?? 0 / 2).strength(0.08));
    forceGraphRef.current?.d3Force("collide", d3.forceCollide((node: SimulationNodeDatum) => node.val - 3));
  }, [width]);

  const drawNode = useCallback((node: NodeObject, canvas: CanvasRenderingContext2D) => {
    const sNode = node as SimulationNode;

    const isHovering = !!hoveredNode;
    let isClusterHovered = false;
    if (hoveredNode !== null) {
      isClusterHovered = hoveredNode.c === sNode.c;
    }

    const color = nodeColorFunction(sNode, canvas);
    const scale = isClusterHovered ? 1.25 : 1;

    canvas.fillStyle = color;
    canvas.globalAlpha = !isHovering || isClusterHovered ? 1 : 0.2;
    canvas.beginPath();
    canvas.arc(sNode.x, sNode.y, (sNode.val + 5) * scale, 0, 2 * Math.PI, false);
    canvas.fill();
    canvas.globalAlpha = 1;
  }, [hoveredNode]);

  const drawLink = useCallback((link: LinkObject, canvas: CanvasRenderingContext2D) => {
      const sLink = link as SimulationLink;
      
      if (!displayLinks || !sLink.source.id || !sLink.target.id) {
        return;
      }

      const isHovering = !!hoveredNode;
      let isClusterHovered = false;
      if (hoveredNode !== null) {
        isClusterHovered = hoveredNode.c === sLink.source.c;
      }

      const linkColor = linkColorFunction(sLink, canvas);
      
      canvas.strokeStyle = linkColor;      
      canvas.globalAlpha = !isHovering || isClusterHovered ? 1 : 0.2;
      canvas.lineWidth = isClusterHovered ? 4 : 2;
      canvas.beginPath();
      canvas.moveTo(sLink.source.x, sLink.source.y);
      canvas.lineTo(sLink.target.x, sLink.target.y);
      canvas.stroke();
      canvas.globalAlpha = 1;
  }, [hoveredNode]);

  const drawLabels = useCallback((canvas: CanvasRenderingContext2D, globalScale: number) => {
    if (labelDisplay < 1) {
      return;
    }

    const activeNodeLabels = [];
    for (let i=0; i<labelDisplay; i++) {
      activeNodeLabels.push(...nodeLabelGroups[i]);
    }

    const fontSize = 12 / globalScale;
    const padding = 0.3;
    const isHovering = !!hoveredNode;
    
    canvas.globalCompositeOperation='source-over';
    canvas.font = `${fontSize}px Sans-Serif`;
    for (const node of activeNodeLabels) {
      let isClusterHovered = false;
      if (hoveredNode !== null) {
        isClusterHovered = hoveredNode.c === node.c;
      }
      if (isHovering && !isClusterHovered) {
        continue;
      }

      const cluster = props.data.clusters[node.c];
      const textWidth = canvas.measureText(cluster.label).width;
      const backgroundDimensions = [textWidth, fontSize].map((n) => n + fontSize * padding);
      const borderDimensions = [textWidth, fontSize].map((n) => n + fontSize * padding * 2);
      const color = nodeColorFunction(node, canvas);

      canvas.fillStyle = color;
      canvas.fillRect(node.x - borderDimensions[0] / 2, node.y - borderDimensions[1] / 2, borderDimensions[0], borderDimensions[1]);
      canvas.beginPath();
      canvas.arc(node.x - borderDimensions[0] / 2, node.y, borderDimensions[1] / 2, 0, 2 * Math.PI, false);
      canvas.arc(node.x + borderDimensions[0] / 2, node.y, borderDimensions[1] / 2, 0, 2 * Math.PI, false);
      canvas.fill();

      canvas.fillStyle = theme.palette.background.default;
      canvas.fillRect(node.x - backgroundDimensions[0] / 2, node.y - backgroundDimensions[1] / 2, backgroundDimensions[0], backgroundDimensions[1]);
      canvas.beginPath();
      canvas.arc(node.x - backgroundDimensions[0] / 2, node.y, backgroundDimensions[1] / 2, 0, 2 * Math.PI, false);
      canvas.arc(node.x + backgroundDimensions[0] / 2, node.y, backgroundDimensions[1] / 2, 0, 2 * Math.PI, false);
      canvas.fill();

      canvas.textAlign = "center";
      canvas.textBaseline = "middle";
      canvas.fillStyle = theme.palette.common.black;
      canvas.fillText(cluster.label, node.x, node.y);
    }
  }, [hoveredNode]);

  const onRenderFramePost = (canvas: CanvasRenderingContext2D, globalScale: number) => {
    scale.current = globalScale;
    if (labelDisplay > 0) {
      drawLabels(canvas, globalScale || 1);
    }
  }
  
  const graphData = useMemo(() => {
    return {
      links: props.data.links,
      nodes: props.data.nodes
    };
  }, []);

  function findNearestNode(event: MouseEvent<HTMLElement>): SimulationNode | null {
    // The mouse coordinates are in window space.
    // They need to be transformed to coordinates over the HTML element that contains the force graph.
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const elementCoords = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    // The HTML element coordinates need to be transformed one more time into the force graph's canvas coordinates.
    const mouseCoords = forceGraphRef.current?.screen2GraphCoords(elementCoords.x, elementCoords.y);
    // const coords = forceGraphRef.current?.screen2GraphCoords(x, y);
    if (!mouseCoords) {
      // If the force graph can't return valid coordinates, nothing is hovered.
      return null;
    }

    // Attempt to find the node nearest to the mouse coordinates.
    // const found = quadtree.current?.find(forceGraphCoords.x, forceGraphCoords.y);
    const nearestNode = quadtree.current?.find(mouseCoords.x, mouseCoords.y);
    if (!nearestNode) {
      // If no node was found, nothing is hovered.
      return null;
    }

    // A "collision" with a node needs to take place when the mouse cursor is close enough to the edge of a node (not the center).
    // The node's size changes based on the zoom level so some calculations need to be done to know where the edge of the node is.
    const vectToMouse = {
      x: mouseCoords.x - nearestNode.x,
      y: mouseCoords.y - nearestNode.y
    };
    const magnitude = Math.sqrt(vectToMouse.x * vectToMouse.x + vectToMouse.y * vectToMouse.y);
    const normalized = {
      x: vectToMouse.x / magnitude,
      y: vectToMouse.y / magnitude
    };
    const radius = nearestNode.val + 5;
    const edgeOffset = {
      x: normalized.x * radius,
      y: normalized.y * radius
    }
    const edgeCoords = {
      x: nearestNode.x + edgeOffset.x,
      y: nearestNode.y + edgeOffset.y
    };
    // Now that the edge coordinates are known, everything has to be transformed back to HTML element coordinates so they can be compared for a "pixel distance" between the mouse and the nearest node.
    const targetCoords = forceGraphRef.current?.graph2ScreenCoords(edgeCoords.x, edgeCoords.y);
    if (!targetCoords) {
      // This shouldn't be possible, but since the force graph can return undefined here something must have went wrong.  Make sure no node is hovered.
      return null;
    }
    
    const distance = Math.sqrt(Math.pow(elementCoords.x - targetCoords.x, 2) + Math.pow(elementCoords.y - targetCoords.y, 2));
    let aimRange = 12;
    if (distance < aimRange) {
      return nearestNode;
    } else {
      return null;
    }
  }

  function onClickNode(node: SimulationNode) {
    const cluster = props.data.clusters[node.c];
    props.onClick?.(node, cluster);
  }

  return (
    <Tooltip
    title={(hoveredNode) ? <ForceGraphTooltip cluster={props.data.clusters[hoveredNode.c]} node={hoveredNode} graph={props.data}/> : ''}
      open={!!hoveredNode}
      followCursor={true}
    >
      <Box
        onClick={(event) => {
          const nearestNode = findNearestNode(event);
          if (nearestNode) {
            onClickNode(nearestNode);
          }
        }}
        onMouseLeave={() => {
          // It is possible to skip the `onHoverNode(null)` event, for example if we are popping up a
          // drilldown dialog.  This check fixes that because the "mouseLeave" event will still fire.  
          // If the mouse has left the bounds of the force graph, or is obstructed by any other 
          // element, we need to deselect the current hovered node.
          setHoveredNode(null);
        }}
        onMouseMove={(event) => {       
          const nearestNode = findNearestNode(event);
          setHoveredNode(nearestNode);
        }}
        ref={ref}
      >
        <ForceGraph2D
          cooldownTicks={100}
          d3AlphaDecay={0.0114}
          d3VelocityDecay={0.2}
          enablePointerInteraction={true}
          graphData={graphData}
          height={props.height}
          linkCanvasObject={drawLink}
          nodeCanvasObject={drawNode}
          onEngineStop={() => {
            forceGraphRef.current?.zoomToFit(400)
            forceGraphRef.current?.pauseAnimation();
            
              quadtree.current = d3.quadtree();
              quadtree.current.x((d) => d.x);
              quadtree.current.y((d) => d.y);
              quadtree.current.addAll(graphData.nodes as SimulationNode[]);
            
          }}
          onNodeClick={(clickedNode: NodeObject) => {
            onClickNode(clickedNode as SimulationNode);
          }}
          onNodeHover={(node: NodeObject | null) => {
            setHoveredNode(node as SimulationNode);
            // When `autoPauseRedraw` is true (default), we need to manually controll the animation 
            // cycle.  One problem with this is that the react-force-graph does not trigger a 
            // re-render when animation is resumed.  This means we can't correctly render hover 
            // effects such as fading nodes and lines or hiding cluster labels. To fix this we need 
            // to make sure the force graph also triggers a re-render when resuming animation.  
            // Looking into the source code, the only efficient way to re-trigger a render is to make 
            // the react-force-graph think the zoom has changed - which causes the force graph to be 
            // marked for a re-render.
            forceGraphRef.current?.zoom(scale.current);
          }}
          onRenderFramePost={onRenderFramePost}
          onZoom={(transform) => {
            scale.current = transform.k;
          }}
          ref={forceGraphRef}
          warmupTicks={20}
          width={width}
        />
      </Box>
    </Tooltip>
  )
}